import { Injectable } from '@angular/core';
import { UrlService } from '../shared/services/url/url.service';
import { CalculatorService } from '../shared/services/calculator/calculator.service';
import { DocumentDataBuilder } from '../document-data/document-data.builder';
import { Estimate } from './estimate.model';
import { DateTimeService } from 'i2g-ng-utils';
import {
  IFetchFilterKey,
  ISearchParams,
  SEARCH_LIST_FILTER,
} from '../entity-data/entity-data.data';
import { EntityTypesEnum } from '../../app.enum';
import {
  ALL_YEARS,
  DocumentTypesEnum,
} from '../document-data/document-data.data';
import {
  DONE_LIST_SEARCH_PARAMS,
  EstimateFiltersEnum,
  EstimateStatusesEnum,
  PENDING_LIST_SEARCH_PARAMS,
} from './estimate-data.data';
import { UtilsService } from '../shared/services/utils/utils.service';
import { Settings } from '../settings-data/settings.model';
import { Toggles } from '../features-data/sub-models/toggles.model';
import { AccountQuotas } from '../features-data/sub-models/account-quotas.model';
import { EmailService } from '../shared/services/email/email.service';
import { TranslateService } from '@ngx-translate/core';
import { MoneyPipe } from '../shared/pipes/money/money.pipe';
import { FriendlyDatePipe } from '../shared/pipes/friendly-date/friendly-date.pipe';

@Injectable()
export class EstimateDataBuilder extends DocumentDataBuilder<Estimate> {
  model = Estimate;
  entityType: EntityTypesEnum = EntityTypesEnum.estimate;
  documentType: DocumentTypesEnum = DocumentTypesEnum.estimate;
  quotaName = AccountQuotas.KEYS.ESTIMATES;
  featureName: string = Toggles.KEYS.ESTIMATE;
  depositFeatureName: string = Toggles.KEYS.DEPOSITS_ON_ESTIMATE;
  listFilterKeys: IFetchFilterKey = {
    [EstimateFiltersEnum.done]: DONE_LIST_SEARCH_PARAMS,
    [EstimateFiltersEnum.pending]: PENDING_LIST_SEARCH_PARAMS,
  };

  constructor(
    dateTimeService: DateTimeService,
    calculatorService: CalculatorService,
    utilsService: UtilsService,
    emailService: EmailService,
    translateService: TranslateService,
    moneyPipe: MoneyPipe,
    friendlyDatePipe: FriendlyDatePipe,
    urlService: UrlService
  ) {
    super(
      dateTimeService,
      calculatorService,
      utilsService,
      emailService,
      translateService,
      moneyPipe,
      friendlyDatePipe,
      urlService
    );
  }

  buildListItemsFilterFn(
    searchParams: ISearchParams,
    listFilter: string,
    settings: Settings
  ): (estimate: Estimate) => boolean {
    const year = +searchParams[this.yearFilterKey];
    const taxYearStart = settings.companySettings.taxYearStart;
    const donePendingFilter =
      listFilter === EstimateFiltersEnum.done
        ? (estimate: Estimate) => this._isDoneEstimate(estimate)
        : (estimate: Estimate) => !this._isDoneEstimate(estimate);
    const yearFilter =
      year && year !== ALL_YEARS
        ? (estimate: Estimate) =>
            this.utilsService.isInTaxYear(taxYearStart, year, estimate.docDate)
        : () => true;

    return listFilter !== SEARCH_LIST_FILTER
      ? (estimate: Estimate) =>
          donePendingFilter(estimate) && yearFilter(estimate)
      : (estimate: Estimate) => yearFilter(estimate);
  }

  buildListFilter(estimate: Estimate): string {
    return this._isDoneEstimate(estimate)
      ? EstimateFiltersEnum.done
      : EstimateFiltersEnum.pending;
  }

  private _isDoneEstimate(estimate: Estimate) {
    return [
      EstimateStatusesEnum.approved,
      EstimateStatusesEnum.declined,
    ].includes(estimate.states.estimateStatus);
  }
}
